import { translate, localHref } from "../i18n";
import LanguageSwitcher from "../components/LanguageSwitcher";
import SiteFooter from "../components/SiteFooter";
import { NAV } from "../content/navigation";
import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { ArrowUpRight, ArrowRight, ArrowDown, List, X, Plus } from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";
import { LoopFilm } from "../components/CinematicHero";
import { QuoteSection, SERVICES } from "./Landing";
import Products from "../components/ProductGallery";
import SeasonalPopup from "../components/SeasonalPopup";
import { ScrollThread, SketchJourney } from "../components/SketchJourney";
import { SEO_SERVICES } from "../content/seoServices";
function Reveal({
  children,
  className = ""
}) {
  const reduced = useReducedMotion();
  return <motion.div className={className} initial={reduced ? false : {
    y: 65,
    opacity: 0
  }} whileInView={{
    y: 0,
    opacity: 1
  }} viewport={{
    once: true,
    amount: 0.15
  }} transition={{
    duration: 0.85,
    ease: [0.16, 1, 0.3, 1]
  }}>
      {children}
    </motion.div>;
}

// Each block enters with the scroll position, then drifts independently.
function ScrollBlock({
  children,
  className = "",
  side = "left"
}) {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const x = useTransform(scrollYProgress, [0, 0.32, 1], [side === "left" ? "-28%" : "28%", "0%", "0%"]);
  const y = useTransform(scrollYProgress, [0, 0.32, 1], [65, 0, -35]);
  const opacity = useTransform(scrollYProgress, [0, 0.65, 0.9, 1], [1, 1, 0.35, 0]);
  return <div ref={ref} className="ph-block-track">
      <motion.div className={"ph-scroll-block " + className} style={reduced ? undefined : {
      x,
      y,
      opacity
    }}>
        {children}
      </motion.div>
    </div>;
}
function Geometry({
  variant = "paper"
}) {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const x = useTransform(scrollYProgress, [0, 0.38, 1], ["-45%", "0%", "8%"]);
  const y = useTransform(scrollYProgress, [0, 1], [90, -95]);
  const rotate = useTransform(scrollYProgress, [0, 1], [-10, 7]);
  const ringX = useTransform(scrollYProgress, [0, 0.45, 1], ["70%", "0%", "-15%"]);
  const ringY = useTransform(scrollYProgress, [0, 1], [110, -130]);
  const textY = useTransform(scrollYProgress, [0, 1], [240, -280]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.72, 1], [0.3, 1, 1, 0]);
  return <div ref={ref} className={"ph-geometry ph-geometry-" + variant} aria-hidden="true">
      <motion.div className="ph-geo-sheet" style={reduced ? undefined : {
      x,
      y,
      rotate,
      opacity
    }} />
      <motion.div className="ph-geo-circle" style={reduced ? undefined : {
      x: ringX,
      y: ringY,
      rotate,
      opacity
    }} />
      <motion.div className="ph-vertical-word" style={reduced ? undefined : {
      y: textY
    }}>
        {{
        paper: translate("CARTA"),
        services: translate("STAMPA"),
        night: translate("STAGIONI"),
        story: "365"
      }[variant]}
      </motion.div>
    </div>;
}
function ProductPanel() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-9%", "9%"]);
  return <div className="ph-detail-video" ref={ref}>
      <motion.img style={reduced ? undefined : {
      y,
      scale: 1.22
    }} src="/images/paper-finishes.webp" alt={translate("Carte materiche e finiture a rilievo: immagine illustrativa")} loading="lazy" width="1024" height="1536" />
      <p>{translate("La qualit\xE0,")}<br />{translate("da vicino.")}</p>
    </div>;
}
export default function PrintHome() {
  const mainRef = useRef(null);
  const threadRef = useRef(null);
  const reduced = useReducedMotion();
  const [menu, setMenu] = useState(false);
  const [idea, setIdea] = useState(() => {
    const product = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("prodotto") : null;
    return product ? {
      title: product,
      service: product,
      edition: translate("Pagina prodotto")
    } : null;
  });
  const {
    scrollYProgress,
    scrollY
  } = useScroll();
  const {
    headerTint,
    headerShape
  } = useHeaderTint(mainRef, scrollY);
  const [showTop, setShowTop] = useState(false);
  useMotionValueEvent(scrollY, "change", latest => setShowTop(latest > 500));
  const scrollCancel = useRef(null);
  useEffect(() => () => scrollCancel.current?.(), []);
  const navigate = useNavigate();
  useEffect(() => {
    if (window.location.hash === "#questo-mese") navigate("/proposte/natale-2026/", {
      replace: true
    });
  }, [navigate]);
  const go = id => {
    if (id === "questo-mese") {
      setMenu(false);
      navigate("/proposte/natale-2026/");
      return;
    }
    setMenu(false);
    scrollCancel.current?.();
    const target = document.getElementById(id);
    if (!target) return;
    const start = window.scrollY;
    const end = Math.max(0, Math.min(document.documentElement.scrollHeight - window.innerHeight, start + target.getBoundingClientRect().top - 100));
    const finish = () => {
      history.replaceState(null, "", "#" + id);
    };
    if (id === "top") {
      window.scrollTo({
        top: 0,
        behavior: "auto"
      });
      finish();
      return;
    }
    if (reduced || Math.abs(end - start) < 10) {
      window.scrollTo({
        top: end,
        behavior: "instant"
      });
      finish();
      return;
    }
    // Menu navigation is a brief transition, independent of the page animations.
    const clock = 450;
    const stages = [{
      from: start,
      to: end,
      start: 0,
      duration: clock
    }];
    let frame;
    const began = performance.now();
    const cancel = () => {
      cancelAnimationFrame(frame);
      ["wheel", "touchstart", "pointerdown", "keydown"].forEach(event => window.removeEventListener(event, cancel));
      scrollCancel.current = null;
    };
    scrollCancel.current = cancel;
    ["wheel", "touchstart", "pointerdown", "keydown"].forEach(event => window.addEventListener(event, cancel, {
      passive: true
    }));
    const tick = now => {
      const elapsed = now - began;
      const stage = stages.find(s => elapsed < s.start + s.duration) || stages[stages.length - 1];
      const t = Math.min(1, Math.max(0, (elapsed - stage.start) / stage.duration));
      const eased = t * t * (3 - 2 * t);
      window.scrollTo({
        top: stage.from + (stage.to - stage.from) * eased,
        behavior: "instant"
      });
      if (elapsed < clock) frame = requestAnimationFrame(tick);else {
        cancel();
        finish();
      }
    };
    frame = requestAnimationFrame(tick);
  };
  const navigateAnchor = e => {
    const anchor = e.target.closest('a[href^="#"]');
    if (!anchor || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const id = anchor.getAttribute("href").slice(1);
    if (document.getElementById(id)) {
      e.preventDefault();
      go(id);
    }
  };
  useEffect(() => {
    const fn = e => {
      if (e.key === "Escape") setMenu(false);
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, []);
  return <div className="ph" ref={threadRef} onClick={navigateAnchor}>
      <motion.div className="ph-progress" style={{
      scaleX: scrollYProgress
    }} aria-hidden="true" />
      <header className="ph-header">
        <motion.div className="ph-header-shape" aria-hidden="true" style={{
        backgroundImage: headerTint,
        clipPath: reduced ? "polygon(0% 0%,100% 0%,100% 52%,0% 100%)" : headerShape
      }} />
        <div className="ph-wrap ph-header-inner">
          <a href={localHref("#top")} className="ph-logo">
            <img src="/images/pedrazzini-print365-640.webp" alt="Pedrazzini Print 365" width="2048" height="387" />
          </a>
          <nav aria-label={translate("Menu principale")}>
            {NAV.map(([id, label]) => <a key={id} href={localHref(id === "questo-mese" ? "/proposte/natale-2026/" : "#" + id)} className={id === "questo-mese" ? "ph-month-menu" : undefined}>
                {label}
              </a>)}
          </nav><LanguageSwitcher />
          <button className="ph-button ph-header-cta" onClick={() => go("contatti")}>{translate("Richiedi un preventivo ")}<ArrowUpRight size={20} />
          </button>
          <button className="ph-menu" aria-label={menu ? translate("Chiudi menu") : translate("Apri menu")} aria-expanded={menu} aria-controls="mobile-menu" onClick={() => setMenu(!menu)}>
            {menu ? <X size={28} /> : <List size={28} />}
          </button>
        </div>
        {menu && <nav id="mobile-menu" className="ph-mobile-nav">
            {NAV.map(([id, label]) => <button key={id} className={id === "questo-mese" ? "ph-month-menu" : undefined} onClick={() => go(id)}>
                {label}
                <ArrowUpRight size={24} />
              </button>)}
            <button onClick={() => go("contatti")}>{translate("Preventivo entro 24 ore ")}<ArrowUpRight size={24} />
            </button>
          </nav>}
      </header>
      {showTop && <motion.button type="button" className="ph-return-tab" aria-label={translate("Torna all\u2019inizio della pagina")} initial={reduced ? false : {
      x: 90
    }} animate={{
      x: 0
    }} whileHover={reduced ? undefined : {
      scale: 1.06
    }} whileFocus={reduced ? undefined : {
      scale: 1.06
    }} transition={reduced ? {
      duration: 0
    } : {
      type: "spring",
      stiffness: 250,
      damping: 23
    }} onClick={() => go("top")}>
          <ArrowDown size={22} className="ph-return-arrow" />
        </motion.button>}
      <main ref={mainRef} className="ph-thread-main">
        <ScrollThread target={threadRef} />
        <Hero go={go} />
        <section className="ph-intro ph-geometry-section">
          <Geometry />
          <div className="ph-wrap">
            <p className="ph-label">PEDRAZZINI / LOCARNO / PRINT 365</p>
            <Reveal>
              <h2>{translate("La differenza si vede.")}<br />
                <span>{translate("E si tocca.")}</span>
              </h2>
            </Reveal>
            <div className="ph-personal-signature">
              <span>{translate("Un mestiere che si tramanda.")}</span>
              <span>{translate("Il preventivo, entro ")}<strong>{translate("24 ore.")}</strong>
              </span>
              <span>{translate("La cura di ogni dettaglio.")}</span>
            </div>
            <div className="ph-intro-bottom">
              <p>{translate("Carta, inchiostro, finiture. Realizziamo stampati per aziende e privati, con la cura di chi conosce la stampa da quattro generazioni.")}</p>
              <a className="ph-link" href={localHref("#servizi")}>{translate("Scopri le possibilit\xE0 ")}<ArrowDown size={22} />
              </a>
            </div>
          </div>
        </section>
        <ProductionFilm />
        <Products onSelect={v => {
        setIdea({
          ...v,
          edition: translate("Catalogo prodotti")
        });
        go("contatti");
      }} />
        <Services />
        <section id="storia" className="ph-story ph-geometry-section">
          <Geometry variant="story" />
          <div className="ph-wrap">
            <p className="ph-label">{translate("PEDRAZZINI, OGGI")}</p>
            <Reveal>
              <h2>{translate("Radici nella stampa.")}<br />
                <span>{translate("Sguardo avanti.")}</span>
              </h2>
            </Reveal>
            <div className="ph-story-grid">
              <ScrollBlock className="ph-story-number" side="left">
                365
                <span>{translate("Un nuovo capitolo,")}<br />{translate("la stessa passione.")}</span>
              </ScrollBlock>
              <div>
                <p className="ph-large-copy">{translate("Quattro generazioni di esperienza.")}<br />{translate("Una nuova energia.")}</p>
                <p>{translate("Dal 2025, Pedrazzini Print 365 fa parte del Gruppo Deco 365. Alla tradizione tipografica di Locarno si aggiungono nuove possibilit\xE0 per la comunicazione della tua attivit\xE0.")}</p>
                <p>{translate("Produzione interna per stampa digitale, tirature variabili e personalizzazioni. Partner specializzati per nobilitazioni, packaging, gadget, abbigliamento e grandi volumi. Un solo referente: noi.")}</p>
                <a className="ph-link" href={localHref("https://www.deco365.ch/")} target="_blank" rel="noreferrer">{translate("Entra nel mondo Deco 365 ")}<ArrowUpRight size={22} />
                </a>
              </div>
            </div>
          </div>
        </section>
        <SketchJourney />
        <QuoteSection selectedIdea={idea} />
      </main>
      <SiteFooter />
      <SeasonalPopup />
    </div>;
}
function Hero({
  go
}) {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: typeof window !== "undefined" && window.innerWidth <= 900 ? ["start start", "end start"] : ["start start", "end end"]
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, -65]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const zoom = useTransform(scrollYProgress, [0, 1], [1, 1.32]);
  return <section className="ph-hero-track" id="top" ref={ref}>
      <div className="ph-hero">
        <motion.div className="ph-hero-image" style={reduced ? undefined : {
        y,
        scale: zoom
      }}>
          <img src="/images/print-editorial-branded.webp" srcSet="/images/print-editorial-branded-1024.webp 1024w, /images/print-editorial-branded.webp 1536w" sizes="(max-width: 640px) 100vw, 100vw" width="1536" height="1024" alt={translate("Brochure e biglietti su carte bianche e blu: ispirazione per il tuo progetto di stampa")} fetchPriority="high" />
        </motion.div>
        <div className="ph-hero-wash" aria-hidden="true" />
        <motion.div className="ph-wrap ph-hero-inner" style={reduced ? undefined : {
        y: titleY
      }}>
          <p className="ph-label">{translate("STAMPA. PERSONALIZZA. DISTINGUITI.")}</p>
          <h1>
            {[translate("Dai forma."), translate("Lascia"), translate("il segno.")].map((word, i) => <span key={word}>
                <motion.span initial={reduced ? false : {
              y: "105%"
            }} animate={{
              y: 0
            }} transition={{
              duration: 0.9,
              delay: i * 0.1,
              ease: [0.16, 1, 0.3, 1]
            }}>
                  {word}
                </motion.span>
              </span>)}
          </h1>
          <p className="ph-hero-copy">{translate("La tua tipografia a Locarno, dalla carta alle finiture.")}<br />{translate("Dalla prima bozza all\u2019ultimo dettaglio.")}</p>
          <button className="ph-button" onClick={() => go("contatti")}>{translate("Il tuo preventivo entro 24 ore ")}<ArrowUpRight size={23} />
          </button>
        </motion.div>
        <div className="ph-hero-caption">
          <span>{translate("CARTA. CARATTERE. POSSIBILIT\xC0.")}</span>
          <a href={localHref("#servizi")} aria-label={translate("Scopri i servizi di stampa")}>
            <ArrowDown size={22} />
          </a>
        </div>
      </div>
    </section>;
}
function ProductionFilm() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.18, 1.05, 1]);
  const copyY = useTransform(scrollYProgress, [0, 1], [65, -65]);
  return <section className="ph-film-section" ref={ref}>
      <motion.div className="ph-film-media" style={reduced ? undefined : {
      scale
    }}>
        <LoopFilm eager src="/video/traditional-sheet-print.mp4" poster="/video/traditional-sheet-print.webp" label={translate("Macchina tradizionale che lavora fogli singoli")} />
      </motion.div>
      <div className="ph-film-overlay" aria-hidden="true" />
      <motion.div className="ph-wrap ph-film-content" style={reduced ? undefined : {
      y: copyY
    }}>
        <p className="ph-label">{translate("LE RADICI DELLA STAMPA")}</p>
        <h2>{translate("Una storia")}<br />{translate("impressa sulla carta.")}</h2>
        <p>{translate("Dalla stampa tradizionale al digitale.")}<br />{translate("Cambiano le possibilit\xE0.")}<br />{translate("Rimane la cura del dettaglio.")}</p>
      </motion.div>
    </section>;
}
function Services() {
  const [active, setActive] = useState(0);
  return <section id="servizi" className="ph-services ph-geometry-section">
      <Geometry variant="services" />
      <div className="ph-wrap">
        <div className="ph-section-heading">
          <div>
            <p className="ph-label">{translate("COSA POSSIAMO REALIZZARE")}</p>
            <Reveal>
              <h2>{translate("Piccolo formato.")}<br />
                <span>{translate("Grandi possibilit\xE0.")}</span>
              </h2>
            </Reveal>
          </div>
          <p>{translate("Dal primo biglietto da visita")}<br />{translate("alla tua prossima campagna.")}</p>
        </div>
        <div className="ph-services-grid">
          <ScrollBlock className="ph-sky-panel" side="left">
            <ProductPanel />
          </ScrollBlock>
          <ScrollBlock className="ph-accordions ph-blue-panel" side="right">
            {SERVICES.map((s, i) => <ServiceItem key={s.title} service={s} index={i} open={active === i} onToggle={() => setActive(active === i ? -1 : i)} />)}
          </ScrollBlock>
        </div>
      </div>
    </section>;
}
function ServiceItem({
  service,
  index,
  open,
  onToggle
}) {
  const reduced = useReducedMotion();
  const duration = reduced ? 0 : 0.5;
  return <article className={open ? "is-open" : ""}>
      <h3>
        <button id={"service-trigger-" + index} aria-expanded={open} aria-controls={"service-" + index} onClick={onToggle}>
          <span className="ph-service-num">0{index + 1}</span>
          <span className="ph-service-title">{service.title}</span>
          <motion.span className="ph-service-toggle" animate={{
          rotate: open ? 135 : 0
        }} transition={{
          duration: reduced ? 0 : 0.45,
          ease: [0.16, 1, 0.3, 1]
        }}>
            <Plus size={24} />
          </motion.span>
        </button>
      </h3>
      <motion.div className="ph-service-panel" id={"service-" + index} role="region" aria-labelledby={"service-trigger-" + index} aria-hidden={!open} inert={!open} initial={false} animate={{
      height: open ? "auto" : 0,
      opacity: open ? 1 : 0
    }} transition={{
      height: {
        duration,
        ease: [0.22, 1, 0.36, 1]
      },
      opacity: {
        duration: reduced ? 0 : 0.25
      }
    }}>
        <div className="ph-service-panel-inner">
          <motion.ul initial={false} animate={open ? "open" : "closed"} variants={{
          open: {
            transition: {
              staggerChildren: reduced ? 0 : 0.045,
              delayChildren: reduced ? 0 : 0.08
            }
          },
          closed: {
            transition: {
              staggerChildren: 0
            }
          }
        }}>
            {service.items.map(item => <motion.li key={item} variants={{
            open: {
              opacity: 1,
              x: 0
            },
            closed: {
              opacity: 0,
              x: reduced ? 0 : 14
            }
          }} transition={{
            duration: reduced ? 0 : 0.3
          }}>
                {item}
              </motion.li>)}
          </motion.ul>
          <div className="ph-service-actions">
            <Link className="ph-link" to={"/servizi/" + SEO_SERVICES[index].slug + "/"} tabIndex={open ? 0 : -1}>{translate("Scopri il servizio ")}<ArrowUpRight size={20} />
            </Link>
            <a className="ph-link" href={localHref("#contatti")} tabIndex={open ? 0 : -1}>{translate("Richiedi preventivo ")}<ArrowUpRight size={20} />
            </a>
          </div>
        </div>
      </motion.div>
    </article>;
}
function useHeaderTint(mainRef, scrollY) {
  const palettes = [["#09256e", "#006de0"], ["#0076ac", "#29c7ee"], ["#112260", "#0752ce"], ["#006fa6", "#019cc5"], ["#102575", "#0069d5"], ["#00648d", "#009bbc"], ["#112263", "#075bda"], ["#00689f", "#009ed3"], ["#0a2573", "#0059c2"]];
  const glass = (hex, alpha) => {
    const value = parseInt(hex.slice(1), 16);
    return `rgba(${value >> 16},${value >> 8 & 255},${value & 255},${alpha})`;
  };
  const gradient = ([a, b]) => `linear-gradient(115deg, ${glass(a, 0.84)} 0%, ${glass(b, 0.72)} 100%)`;
  const silhouettes = ["polygon(0% 0%,100% 0%,100% 54%,0% 100%)", "polygon(0% 0%,100% 0%,100% 100%,0% 54%)"];
  const [stops, setStops] = useState({
    positions: [0, 1000],
    colors: [gradient(palettes[0]), gradient(palettes[1])],
    shapes: silhouettes
  });
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    let frame;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const sections = Array.from(main.querySelectorAll(":scope > section"));
        const positions = [0],
          colors = [gradient(palettes[0])],
          shapes = [silhouettes[0]];
        sections.slice(1).forEach((section, index) => {
          const i = index + 1;
          const boundary = section.getBoundingClientRect().top + window.scrollY;
          const start = Math.max(positions[positions.length - 1] + 1, boundary - 310);
          positions.push(start, Math.max(start + 1, boundary - 65));
          colors.push(gradient(palettes[Math.min(i - 1, palettes.length - 1)]), gradient(palettes[Math.min(i, palettes.length - 1)]));
          shapes.push(silhouettes[(i - 1) % 2], silhouettes[i % 2]);
        });
        setStops({
          positions,
          colors,
          shapes
        });
      });
    };
    const observer = new ResizeObserver(measure);
    observer.observe(main);
    window.addEventListener("resize", measure);
    measure();
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      cancelAnimationFrame(frame);
    };
    // Fixed palettes and shapes; recalculate only the actual section boundaries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainRef]);
  const headerTint = useTransform(scrollY, stops.positions, stops.colors);
  const headerShape = useTransform(scrollY, stops.positions, stops.shapes);
  return {
    headerTint,
    headerShape
  };
}
